import React, { useState, useEffect, useRef } from "react";
import { faTrash } from "@fortawesome/pro-regular-svg-icons/faTrash";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Button,
  TextField,
  IconButton,
  Box,
  ListItemText,
  Menu,
  MenuList,
  Divider,
  ListItem,
  ListItemIcon,
} from "@vapor/react-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@vapor/react-extended";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 2;

interface SelectMultipleCheckboxesProps {
  options?: string[];
  width?: number | string;
  label?: string;
  handleCheckboxChange: any;
  documentType?: string;
}

const names = ["Verificare", "In scadenza", "Verificati"];

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 100,
    },
  },
};

const SelectCheckboxesArray: React.FC<SelectMultipleCheckboxesProps> = ({
  options,
  width = 200,
  label = "Tag",
  handleCheckboxChange,
  documentType = "",
}: SelectMultipleCheckboxesProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOptions, setMenuOptions] = useState<string[]>(options ?? names);
  const [selections, setSelections] = useState<string[]>([]);
  const [isAddingNewOption, setIsAddingNewOption] = useState(false);
  const [newOption, setNewOption] = useState<string>("");
  const [openSelect, setOpenSelect] = useState(false);

  const selectRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (documentType === "Tutto l'anno") {
      setSelections(menuOptions);
    }
  }, [documentType, menuOptions]);

  useEffect(() => {
    const filteredSelections = selections.filter((tag) => tag && tag.trim());
    handleCheckboxChange(filteredSelections);
  }, [selections]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    const filteredValue = typeof value === "string" ? value.split(",") : value;
    setSelections(filteredValue.filter((tag) => tag && tag.trim()));
  };

  const handleAddNewOption = () => {
    const trimmedOption = newOption.trim();
    if (trimmedOption) {
      setSelections([...selections, trimmedOption]);
      setMenuOptions([...menuOptions, trimmedOption]);
      setNewOption("");
      setIsAddingNewOption(false);
      setAnchorEl(null);
    }
  };

  const handleCancelNewOption = () => {
    setIsAddingNewOption(false);
    setNewOption("");
    setAnchorEl(null);
  };

  const handleOpenAddOption = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setIsAddingNewOption(true);
    setOpenSelect(true);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
      <FormControl sx={{ width: width }} ref={selectRef}>
        <InputLabel id="demo-multiple-checkbox-label">{label}</InputLabel>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          multiple
          value={selections}
          onChange={handleChange}
          input={<OutlinedInput label="Tag" />}
          renderValue={(selected: []) =>
            !!selected ? selected.join(", ") : "Apri opzioni"
          }
          MenuProps={MenuProps}
          open={openSelect}
          onOpen={() => setOpenSelect(true)}
          onClose={() => setOpenSelect(false)}
        >
          {menuOptions.map((name) => (
            <MenuItem sx={{ p: 0, width: width }} key={name} value={name}>
              <Checkbox checked={selections.indexOf(name) > -1} />
              <ListItemText primary={name} />
            </MenuItem>
          ))}

          <MenuItem sx={{ p: 0, width: width }} onClick={handleOpenAddOption}>
            <Button
              variant="text"
              color="primary"
              sx={{ fontSize: "12px", fontWeight: "400" }}
            >
              Crea Nuovo
            </Button>
          </MenuItem>
        </Select>
      </FormControl>

      {isAddingNewOption && (
        <Menu
          id={`edit-menu-popover-${width}`}
          open={isAddingNewOption}
          anchorEl={anchorEl}
          onClose={handleCancelNewOption}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuList>
            <ListItem>
              <TextField
                inputRef={selectRef}
                value={newOption}
                label={"Add new option"}
                onChange={(e) => setNewOption(e.target.value)}
                onClick={(e) => {
                  e.stopPropagation();
                  selectRef?.current?.focus();
                }}
                inputMode="text"
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    handleAddNewOption();
                  }
                }}
              />
            </ListItem>
            <Divider
              sx={{
                margin: 0,
                "&.MuiDivider-root.MuiDivider-fullWidth": {
                  height: "1px",
                },
              }}
            />
            <MenuItem onClick={handleCancelNewOption}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faTrash} style={{ color: "red" }} />
              </ListItemIcon>
              <Typography style={{ color: "red" }}>Delete </Typography>
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Box>
  );
};

export default SelectCheckboxesArray;
